// ============================================================================
// Portfolio KPI Widget — top-of-dashboard scan row for Bryan's CEO view.
// Four numbers, no chrome, no nesting: ARR · Annual Margin (weighted by ARR)
// · Active Sites · States Served.
//
// Source: PortfolioSummary aggregate in use-dashboard-data.ts. The margin is
// ARR-weighted so a single $300k hospital running at 18% can't be cancelled
// by a $1k/mo trial running at 80%.
//
// Layout: four equal cells in a row on desktop; 2x2 grid on mobile. Sits
// above the existing widget grid as a separate visual band — these are
// "scan headlines" not "drill widgets," so they don't share the card chrome.
// ============================================================================

import type { PortfolioSummary } from './use-dashboard-data';
import { Card, MetaLabel, Skeleton } from '../../shared/components';
import { fmtCompactCurrency } from './widget-card';

interface Props {
  data: PortfolioSummary | null;
  loading: boolean;
}

export function PortfolioKpiWidget({ data, loading }: Props) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiCell
        label="Annual Revenue"
        value={data ? fmtCompactCurrency(data.total_arr) : null}
        sub={
          data
            ? `${data.active_site_count} active sites`
            : 'Active + billing'
        }
        loading={loading && !data}
      />
      <KpiCell
        label="Annual Margin"
        value={
          data
            ? `${(data.weighted_margin_pct * 100).toFixed(1)}%`
            : null
        }
        sub={
          data
            ? `${fmtCompactCurrency(data.total_annual_margin)} annual · weighted by ARR`
            : 'ARR-weighted gross margin'
        }
        loading={loading && !data}
      />
      <KpiCell
        label="Active Sites"
        value={data ? String(data.active_site_count) : null}
        sub={
          data
            ? `${data.margin_observed_count} with margin observable`
            : 'Across all segments'
        }
        loading={loading && !data}
      />
      <KpiCell
        label="States Served"
        value={data ? String(data.states_served_count) : null}
        sub="ASC + Dialysis footprint"
        loading={loading && !data}
      />
    </div>
  );
}

function KpiCell({
  label,
  value,
  sub,
  loading,
}: {
  label: string;
  value: string | null;
  sub: string;
  loading: boolean;
}) {
  return (
    <Card as="div" className="p-4">
      <MetaLabel>{label}</MetaLabel>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-tcs-text">
        {loading ? (
          <Skeleton block height={28} width="60%" />
        ) : (
          (value ?? '—')
        )}
      </div>
      <div className="mt-1 text-xs text-tcs-text-muted">{sub}</div>
    </Card>
  );
}
